import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Briefcase, FolderKanban, ListChecks, FolderOpen,
  Sparkles, Users2, LogOut, HardHat, Bell, Search, ChevronDown, Truck, Camera,
  Wifi, WifiOff, RefreshCw, Loader2, Calendar, Settings, Shield, User as UserIcon,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { ROLE_LABELS, initials } from "../lib/api";
import { useBranding } from "../lib/branding";
import { useEffect, useState } from "react";
import { pendingCount as idbPending } from "../lib/idb";
import { syncPending } from "../lib/sync";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from "./ui/dropdown-menu";
import NotificationsBell from "./NotificationsBell";

const NAV = [
  { to: "/dashboard", label: "Tableau de bord", icon: LayoutDashboard, roles: ["admin","chef_projet","expert","client"] },
  { to: "/projects", label: "Projets", icon: Briefcase, roles: ["admin","chef_projet","expert","client"] },
  { to: "/tasks", label: "Tâches", icon: ListChecks, roles: ["admin","chef_projet","expert"] },
  { to: "/ged", label: "GED Documents", icon: FolderOpen, roles: ["admin","chef_projet","expert","client"] },
  { to: "/logistique", label: "Logistique", icon: Truck, roles: ["admin","chef_projet","expert"] },
  { to: "/terrain", label: "Terrain (offline)", icon: Camera, roles: ["admin","chef_projet","expert"] },
  { to: "/calendrier", label: "Calendrier", icon: Calendar, roles: ["admin","chef_projet","expert","client"] },
  { to: "/forms", label: "Formulaires", icon: FolderKanban, roles: ["admin","chef_projet","expert"] },
  { to: "/ai", label: "Assistant IA", icon: Sparkles, roles: ["admin","chef_projet"] },
  { to: "/users", label: "Utilisateurs", icon: Users2, roles: ["super_admin","admin"] },
  { to: "/roles", label: "Rôles", icon: Shield, roles: ["super_admin"] },
  { to: "/configuration", label: "Configuration", icon: Settings, roles: ["super_admin"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const branding = useBranding();

  if (!user) return null;
  const visible = NAV.filter((n) => user.role === "super_admin" || n.roles.includes(user.role));

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside className="w-64 bg-btp-navy-deep text-slate-300 flex flex-col fixed inset-y-0 left-0 z-30" data-testid="sidebar">
        <div className="h-16 flex items-center gap-2 px-5 border-b border-btp-sidebar">
          <div className="w-9 h-9 rounded-md bg-btp-terra flex items-center justify-center overflow-hidden">
            {branding.company_logo_url ? (
              <img src={branding.company_logo_url} alt="logo" className="w-full h-full object-cover" />
            ) : (
              <HardHat size={20} className="text-white" />
            )}
          </div>
          <div className="min-w-0">
            <div className="text-white font-heading font-bold text-sm tracking-tight truncate" data-testid="sidebar-brand-name">{branding.company_name}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 truncate">{branding.company_tagline}</div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
          <div className="px-2 mb-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Navigation</div>
          {visible.map((item) => (
              <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.to.replace("/", "")}`}
              className={({ isActive }) =>
                `sidebar-link ${isActive ? "sidebar-link-active" : ""}`
              }
            >
              <item.icon size={18} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-btp-sidebar">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-btp-terra flex items-center justify-center text-white font-semibold text-sm">
              {initials(user.full_name)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white text-sm font-medium truncate">{user.full_name}</div>
              <div className="text-[11px] text-slate-400">{ROLE_LABELS[user.role]}</div>
            </div>
            <button onClick={logout} className="text-slate-400 hover:text-white" data-testid="logout-btn" title="Déconnexion">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 ml-64 flex flex-col min-h-screen">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center px-6 gap-4 sticky top-0 z-20">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Rechercher un projet, document, tâche…"
                className="w-full h-10 pl-10 pr-4 text-sm border border-slate-200 rounded-md bg-slate-50 focus:bg-white focus:border-btp-navy focus:outline-none focus:ring-1 focus:ring-btp-navy"
                data-testid="topbar-search"
              />
            </div>
          </div>
          <OnlineIndicator />
          <NotificationsBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 rounded-md" data-testid="user-menu-trigger">
                <div className="w-8 h-8 rounded-full bg-btp-navy text-white flex items-center justify-center text-xs font-semibold">
                  {initials(user.full_name)}
                </div>
                <ChevronDown size={14} className="text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="font-semibold text-sm">{user.full_name}</div>
                <div className="text-xs text-slate-500">{user.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                <UserIcon size={14} className="mr-2" /> Mon profil
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">Tableau de bord</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-red-600" data-testid="menu-logout">
                <LogOut size={14} className="mr-2" /> Déconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <main className="flex-1 p-6 lg:p-8 fade-in">{children}</main>
      </div>
    </div>
  );
}

function OnlineIndicator() {
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);

  const refresh = async () => {
    try { setPending(await idbPending()); } catch { /* idb not available */ }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 4000);
    const onOnline = () => { setOnline(true); doSync(); };
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => { clearInterval(t); window.removeEventListener("online", onOnline); window.removeEventListener("offline", onOffline); };
    // eslint-disable-next-line
  }, []);

  const doSync = async () => {
    if (!navigator.onLine || syncing) return;
    setSyncing(true);
    try { await syncPending(); } finally { setSyncing(false); refresh(); }
  };

  return (
    <button onClick={doSync} disabled={!online || syncing}
      className={`flex items-center gap-1.5 h-9 px-3 rounded-md text-xs font-semibold border transition-colors ${
        online ? "border-emerald-200 text-emerald-700 bg-emerald-50 hover:bg-emerald-100" : "border-amber-200 text-amber-800 bg-amber-50"
      } ${pending > 0 ? "ring-1 ring-btp-terra/30" : ""}`}
      title={online ? `${pending} saisie(s) en attente — cliquer pour synchroniser` : "Hors-ligne"}
      data-testid="online-indicator">
      {syncing ? <Loader2 size={13} className="animate-spin" /> : online ? <Wifi size={13} /> : <WifiOff size={13} />}
      <span className="hidden sm:inline">{online ? "En ligne" : "Hors-ligne"}</span>
      {pending > 0 && (
        <span className="ml-1 px-1.5 py-0.5 rounded-full bg-btp-terra text-white text-[10px] font-bold tabular-nums" data-testid="pending-badge">
          {pending}
        </span>
      )}
    </button>
  );
}
